import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, Mail, Phone, MessageCircle, Download,
  Save, FileText, Calendar, Globe, ShoppingBag,
  CheckCircle2, Instagram,
} from "lucide-react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import AdminLayout from "./AdminLayout";
import SocialPostModal from "./SocialPostModal";
import { adminApi, formatApiError } from "@/lib/adminApi";

const STATUS_OPTIONS = [
  { key: "new",       label: "Nuevo",      bg: "bg-[#2c4a3b]/10 text-[#2c4a3b]" },
  { key: "contacted", label: "Contactado", bg: "bg-[#bc6c25]/15 text-[#9c581e]" },
  { key: "converted", label: "Convertido", bg: "bg-emerald-500/15 text-emerald-700" },
  { key: "lost",      label: "Perdido",    bg: "bg-red-500/10 text-red-600" },
];

export default function AdminLeadDetail() {
  const { id } = useParams();
  const [lead, setLead] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState("");
  const [socialModalOpen, setSocialModalOpen] = useState(false);
  const protocolRef = useRef(null);

  const load = useCallback(async () => {
    setError("");
    try {
      const data = await adminApi.getLead(id);
      setLead(data);
      setNotes(data.notes || "");
    } catch (err) {
      setError(formatApiError(err));
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (status) => {
    if (status === "converted" && lead.status !== "converted") {
      const ok = window.confirm(
        `¿Marcar a ${lead.name.split(" ")[0]} como CONVERTIDO?\n\n` +
        `Se enviará un email automático de bienvenida con un tip de uso del Mes 1 de su protocolo.`
      );
      if (!ok) return;
    }
    setSaving(true);
    try {
      const updated = await adminApi.updateLead(id, { status });
      setLead(updated);
      if (status === "converted") {
        toast.success("¡Felicidades! Email de bienvenida enviado al cliente.", {
          description: "Marcado como convertido y notificado por correo.",
        });
      } else {
        toast.success("Estado actualizado");
      }
    } catch (err) {
      setError(formatApiError(err));
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const saveNotes = async () => {
    setSaving(true);
    try {
      const updated = await adminApi.updateLead(id, { notes });
      setLead(updated);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const phoneDigits = useMemo(() => (lead?.phone || "").replace(/\D/g, ""), [lead]);
  const waLink = phoneDigits
    ? `https://wa.me/${phoneDigits}?text=${encodeURIComponent(
        `Hola ${lead.name.split(" ")[0]}, soy Sarai de Eliora Longevity. ¡Vi tu protocolo personalizado! ✨ ¿Cómo puedo ayudarte a empezar?`
      )}`
    : null;

  const downloadProtocolPdf = async () => {
    if (!protocolRef.current) return;
    const canvas = await html2canvas(protocolRef.current, {
      backgroundColor: "#f9f6f0",
      scale: 2,
      useCORS: true,
      logging: false,
    });
    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const imgH = (canvas.height * pageW) / canvas.width;
    if (imgH <= pageH) {
      pdf.addImage(canvas.toDataURL("image/jpeg", 0.92), "JPEG", 0, 0, pageW, imgH);
    } else {
      // Smart slice based on top-level children to avoid mid-block cuts
      const root = protocolRef.current;
      const rootRect = root.getBoundingClientRect();
      const scaleY = canvas.height / rootRect.height;
      const pageHpx = (pageH * canvas.width) / pageW;
      const breaks = new Set([0, canvas.height]);
      const walk = (el, d = 0) => {
        if (d > 5) return;
        for (const c of el.children) {
          const r = c.getBoundingClientRect();
          const top = Math.round((r.top - rootRect.top) * scaleY);
          const bot = Math.round((r.bottom - rootRect.top) * scaleY);
          if (bot - top > 8) { breaks.add(top); breaks.add(bot); }
          walk(c, d + 1);
        }
      };
      walk(root);
      const sorted = [...breaks].sort((a, b) => a - b);
      const findNext = (y) => {
        const cap = y + pageHpx;
        let best = y;
        for (const b of sorted) {
          if (b > y && b <= cap) best = b;
          if (b > cap) break;
        }
        if (best === y) best = Math.min(y + pageHpx, canvas.height);
        return best;
      };
      let y = 0, page = 0;
      while (y < canvas.height - 1) {
        const yEnd = findNext(y);
        const h = yEnd - y;
        const slice = document.createElement("canvas");
        slice.width = canvas.width; slice.height = h;
        const ctx = slice.getContext("2d");
        ctx.fillStyle = "#f9f6f0"; ctx.fillRect(0, 0, slice.width, slice.height);
        ctx.drawImage(canvas, 0, y, canvas.width, h, 0, 0, canvas.width, h);
        const hMm = (h * pageW) / canvas.width;
        if (page > 0) pdf.addPage();
        pdf.addImage(slice.toDataURL("image/jpeg", 0.92), "JPEG", 0, 0, pageW, hMm);
        y = yEnd; page += 1;
      }
    }
    pdf.save(`protocolo-${lead.name.split(" ")[0]}.pdf`);
  };

  if (error && !lead) {
    return (
      <AdminLayout>
        <p className="text-red-600">{error}</p>
        <Link to="/admin" className="text-[#bc6c25] mt-4 inline-flex items-center">
          <ArrowLeft className="w-4 h-4 mr-2" /> Volver
        </Link>
      </AdminLayout>
    );
  }
  if (!lead) return <AdminLayout><p className="text-[#5c6b61]">Cargando…</p></AdminLayout>;

  const answers = lead.answers || [];
  const protocol = lead.protocol;

  return (
    <AdminLayout>
      <Link
        to="/admin"
        data-testid="back-to-dashboard"
        className="inline-flex items-center gap-2 text-sm text-[#5c6b61] hover:text-[#1a2421] mb-6"
      >
        <ArrowLeft className="w-4 h-4" /> Volver al dashboard
      </Link>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* ---------- LEFT: lead info ---------- */}
        <aside className="space-y-4">
          <div className="bg-white rounded-3xl p-6 border border-[#2c4a3b]/8" data-testid="lead-info-card">
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#bc6c25]">Lead</p>
            <h1 className="font-serif text-3xl mt-1 text-[#1a2421] leading-tight">{lead.name}</h1>

            <div className="mt-5 space-y-2 text-sm">
              <p className="flex items-center gap-2 text-[#1a2421]">
                <Mail className="w-3.5 h-3.5 text-[#5c6b61]" />
                <a href={`mailto:${lead.email}`} className="hover:text-[#bc6c25]">{lead.email}</a>
              </p>
              <p className="flex items-center gap-2 text-[#1a2421]">
                <Phone className="w-3.5 h-3.5 text-[#5c6b61]" />
                {lead.phone || "—"}
              </p>
              <p className="flex items-center gap-2 text-[#5c6b61] text-xs">
                <Globe className="w-3.5 h-3.5" /> {lead.language?.toUpperCase()}
                <span className="mx-1">·</span>
                <Calendar className="w-3.5 h-3.5" />
                {new Date(lead.created_at).toLocaleString("es-MX")}
              </p>
              <p className="flex items-center gap-2 text-[#5c6b61] text-xs">
                <ShoppingBag className="w-3.5 h-3.5" /> Origen: <strong className="text-[#1a2421]">{lead.source}</strong>
              </p>
            </div>

            <div className="mt-5 space-y-2">
              {/* Big primary CTA: mark as converted */}
              {lead.status !== "converted" && (
                <button
                  onClick={() => updateStatus("converted")}
                  disabled={saving}
                  data-testid="mark-converted-btn"
                  className="w-full group inline-flex items-center justify-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white py-3.5 text-sm font-semibold shadow-lg shadow-emerald-600/20 transition-all disabled:opacity-60"
                >
                  <CheckCircle2 className="w-5 h-5" />
                  ¡Compró! Marcar como convertido
                </button>
              )}
              {lead.status === "converted" && (
                <div className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 py-3 text-sm font-semibold">
                  <CheckCircle2 className="w-5 h-5" />
                  Cliente convertido ✓
                </div>
              )}
              {waLink && (
                <a
                  href={waLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-btn"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-white py-3 text-sm font-semibold hover:bg-[#1eb854]"
                >
                  <MessageCircle className="w-4 h-4" /> Abrir WhatsApp
                </a>
              )}
              <a
                href={`mailto:${lead.email}`}
                data-testid="contact-email-btn"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-[#2c4a3b]/30 text-[#1a2421] py-3 text-sm font-semibold hover:bg-[#2c4a3b]/5"
              >
                <Mail className="w-4 h-4" /> Enviar email
              </a>
              {protocol && (
                <Button
                  onClick={downloadProtocolPdf}
                  data-testid="download-lead-pdf"
                  variant="outline"
                  className="w-full rounded-full border-[#2c4a3b]/30 text-[#1a2421]"
                >
                  <Download className="w-4 h-4 mr-2" /> Descargar protocolo (PDF)
                </Button>
              )}
              {lead.status === "converted" && (
                <Button
                  onClick={() => setSocialModalOpen(true)}
                  data-testid="open-social-modal-btn"
                  className="w-full rounded-full bg-gradient-to-r from-[#bc6c25] to-[#9c581e] hover:opacity-90 text-white"
                >
                  <Instagram className="w-4 h-4 mr-2" /> Generar post para Instagram
                </Button>
              )}
            </div>
          </div>

          {/* ---------- Status ---------- */}
          <div className="bg-white rounded-3xl p-6 border border-[#2c4a3b]/8" data-testid="status-card">
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#5c6b61] mb-4">Estado del lead</p>
            <div className="grid grid-cols-2 gap-2">
              {STATUS_OPTIONS.map((opt) => (
                <button
                  key={opt.key}
                  data-testid={`status-${opt.key}`}
                  disabled={saving}
                  onClick={() => updateStatus(opt.key)}
                  className={`rounded-full px-3 py-2 text-xs font-semibold uppercase tracking-wide transition-all ${
                    lead.status === opt.key
                      ? `${opt.bg} ring-2 ring-current`
                      : "bg-[#f9f6f0] text-[#5c6b61] hover:bg-[#f0ebe1]"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* ---------- Notes ---------- */}
          <div className="bg-white rounded-3xl p-6 border border-[#2c4a3b]/8">
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#5c6b61] mb-3">Notas privadas</p>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="notes-textarea"
              rows={5}
              placeholder="Llamé el lunes 10:30am, le interesa Immunocal Sport…"
              className="border-[#2c4a3b]/15 rounded-xl"
            />
            <Button
              onClick={saveNotes}
              disabled={saving}
              data-testid="save-notes-btn"
              className="mt-3 w-full rounded-full bg-[#2c4a3b] hover:bg-[#1e3328] text-white"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Guardando…" : "Guardar notas"}
            </Button>
          </div>
        </aside>

        {/* ---------- RIGHT: quiz + protocol ---------- */}
        <section className="lg:col-span-2 space-y-6">
          {answers.length > 0 && (
            <div className="bg-white rounded-3xl p-7 border border-[#2c4a3b]/8">
              <p className="text-[10px] uppercase tracking-[0.22em] text-[#bc6c25] mb-4">Respuestas del Quiz</p>
              <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                {answers.map((a, i) => (
                  <li key={i} className="border-b border-[#2c4a3b]/5 pb-2">
                    <p className="text-xs text-[#5c6b61]">{a.question}</p>
                    <p className="text-[#1a2421] font-medium mt-0.5">{a.answer}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {protocol && (
            <div
              ref={protocolRef}
              data-testid="protocol-snapshot"
              className="bg-[#f9f6f0] rounded-3xl p-8 border border-[#2c4a3b]/8 space-y-6"
            >
              <div>
                <div className="flex items-center gap-2 text-[#bc6c25]">
                  <FileText className="w-4 h-4" />
                  <p className="text-[10px] uppercase tracking-[0.22em]">Protocolo de cortesía · 3 meses</p>
                </div>
                <h2 className="font-serif text-3xl mt-2 text-[#1a2421]">{lead.name.split(" ")[0]}</h2>
                <p className="text-sm text-[#5c6b61] mt-3 leading-relaxed">{protocol.summary}</p>
              </div>

              {protocol.key_concerns?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {protocol.key_concerns.map((c, i) => (
                    <span key={i} className="bg-[#bc6c25]/12 text-[#9c581e] rounded-full px-3 py-1 text-xs font-semibold">
                      {c}
                    </span>
                  ))}
                </div>
              )}

              {protocol.months?.map((m) => (
                <div key={m.month} className="bg-white rounded-2xl p-5 border border-[#2c4a3b]/8">
                  <p className="text-[10px] uppercase tracking-[0.22em] text-[#bc6c25]">Mes {m.month}</p>
                  <h3 className="font-serif text-xl mt-1 text-[#1a2421]">{m.title}</h3>
                  <p className="text-xs italic text-[#5c6b61] mt-1">{m.focus}</p>
                  <ul className="mt-3 space-y-2">
                    {m.products?.map((p, i) => (
                      <li key={i} className="text-sm">
                        <strong className="text-[#1a2421]">{p.name}</strong>
                        <span className="text-[#5c6b61]"> — {p.dosage}</span>
                        <p className="text-xs text-[#5c6b61] mt-0.5">{p.reason}</p>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

              {protocol.closing_note && (
                <div className="bg-[#2c4a3b] text-[#f9f6f0] rounded-2xl p-5">
                  <p className="text-[10px] uppercase tracking-[0.22em] text-[#bc6c25]">Nota de Sarai</p>
                  <p className="font-serif italic mt-2 leading-relaxed">{protocol.closing_note}</p>
                </div>
              )}
            </div>
          )}
        </section>
      </div>

      <SocialPostModal
        open={socialModalOpen}
        onClose={() => setSocialModalOpen(false)}
        leadId={lead.id}
        leadName={lead.name}
      />
    </AdminLayout>
  );
}
